/* ==========================================================================
   FLUXAIM — floating header pill ("dynamic island")
   Self-contained: no dependency on Horizon's header-component.

   Open/closed state lives entirely in CSS, driven by attributes. Nothing is
   ever hidden by opacity alone, so a closed panel can never swallow clicks.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  // Two thresholds, so a slow or momentum scroll that hovers around one line
  // cannot flip the bar back and forth.
  var COLLAPSE_AT = 56;
  var EXPAND_AT = 16;
  var HOVER_CLOSE_DELAY = 320;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function init() {
    var header = document.querySelector('[data-fa-header]');
    if (!header) return;

    if (header.dataset.faReady === 'true') return;
    header.dataset.faReady = 'true';

    var pill = header.querySelector('[data-fa-pill]');
    var megaTrigger = header.querySelector('[data-fa-mega-trigger]');
    var mega = header.querySelector('[data-fa-mega]');
    var burger = header.querySelector('[data-fa-burger]');
    var sheet = document.querySelector('[data-fa-sheet]');
    var sheetClose = sheet ? sheet.querySelector('[data-fa-sheet-close]') : null;
    var scrim = document.querySelector('[data-fa-scrim]');

    /* ---------------------------------------------------------------
       Header height custom properties (Horizon components read these)
       --------------------------------------------------------------- */
    var logoEl = header.querySelector('.fa-hd__logo');
    var measured = {};

    // Only writes a property when its value changes. A custom property set on
    // <body> restyles the whole page, so this must never run every frame.
    function setVar(el, name, value) {
      var key = (el === header ? 'h' : 'b') + name;
      if (measured[key] === value) return;
      measured[key] = value;
      el.style.setProperty(name, value);
    }

    // The bar's resting size, read from its settings rather than from the
    // live row: while the bar folds and unfolds its height is in motion, and
    // what sits under it must not move with it.
    function measure() {
      var cs = getComputedStyle(header);
      var h = parseInt(cs.getPropertyValue('--fa-hd-h'), 10) || 58;
      var top = parseInt(cs.getPropertyValue('--fa-hd-top'), 10) || 20;
      setVar(document.body, '--header-height', h + top + 8 + 'px');
      setVar(document.body, '--header-group-height', '0px');
      setVar(document.body, '--transparent-header-offset-boolean', '0');

      var logoWidth = logoEl ? logoEl.offsetWidth : 70;
      var collapsed = Math.min(340, Math.max(168, Math.round(logoWidth + 108)));
      setVar(header, '--fa-hd-collapsed', collapsed + 'px');
    }

    var measureQueued = false;
    function queueMeasure() {
      if (measureQueued) return;
      measureQueued = true;
      window.requestAnimationFrame(function () {
        measureQueued = false;
        measure();
      });
    }

    measure();
    window.addEventListener('resize', queueMeasure, { passive: true });
    window.addEventListener('load', queueMeasure, { once: true });
    var logoImg = logoEl ? logoEl.querySelector('img') : null;
    if (logoImg && !logoImg.complete) logoImg.addEventListener('load', queueMeasure, { once: true });

    /* ---------------------------------------------------------------
       Scroll state: the pill collapses to logo + actions
       --------------------------------------------------------------- */
    var scrolled = false;
    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var y = window.scrollY;
        var next = scrolled ? y > EXPAND_AT : y > COLLAPSE_AT;
        if (next !== scrolled) {
          scrolled = next;
          root.toggleAttribute('data-fa-scrolled', scrolled);
          if (scrolled) closeMega();
        }
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* ---------------------------------------------------------------
       Shop panel
       --------------------------------------------------------------- */
    var megaOpen = false;
    var openedByHover = false;
    var hoverTimer = null;
    var openedAt = 0;

    function openMega() {
      if (!mega || megaOpen) return;
      megaOpen = true;
      openedAt = Date.now();
      header.setAttribute('data-mega', 'open');
      if (megaTrigger) megaTrigger.setAttribute('aria-expanded', 'true');
    }

    function closeMega() {
      if (!mega || !megaOpen) return;
      megaOpen = false;
      openedByHover = false;
      window.clearTimeout(hoverTimer);
      header.removeAttribute('data-mega');
      if (megaTrigger) megaTrigger.setAttribute('aria-expanded', 'false');
    }

    if (megaTrigger && mega) {
      var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      megaTrigger.addEventListener('click', function (event) {
        event.preventDefault();
        // On hover devices the panel is already open when the click lands.
        if (openedByHover) {
          openedByHover = false;
          return;
        }
        if (megaOpen) closeMega();
        else openMega();
      });

      if (canHover) {
        megaTrigger.addEventListener('mouseenter', function () {
          window.clearTimeout(hoverTimer);
          if (!megaOpen) openedByHover = true;
          openMega();
        });

        // Closing is decided from the pointer position rather than from
        // mouseleave: while the panel is still growing the cursor can sit in
        // space the bar has not covered yet, and enter/leave events fire in an
        // order that used to shut the panel just as you reached the links.
        document.addEventListener(
          'pointermove',
          function (event) {
            if (!megaOpen) return;

            // Ignore the first moments after opening, while the panel grows.
            if (Date.now() - openedAt < 450) {
              window.clearTimeout(hoverTimer);
              hoverTimer = null;
              return;
            }

            var box = pill.getBoundingClientRect();
            var pad = 28;
            var inside =
              event.clientX >= box.left - pad &&
              event.clientX <= box.right + pad &&
              event.clientY >= box.top - pad &&
              event.clientY <= box.bottom + pad;

            if (inside) {
              window.clearTimeout(hoverTimer);
              hoverTimer = null;
            } else if (!hoverTimer) {
              hoverTimer = window.setTimeout(closeMega, HOVER_CLOSE_DELAY);
            }
          },
          { passive: true }
        );
      }

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeMega();
      });

      document.addEventListener('click', function (event) {
        if (megaOpen && !pill.contains(event.target)) closeMega();
      });

      // Keyboard users leaving the panel by tabbing out
      pill.addEventListener('focusout', function (event) {
        if (megaOpen && event.relatedTarget && !pill.contains(event.relatedTarget)) closeMega();
      });
    }

    /* ---------------------------------------------------------------
       Mobile sheet
       --------------------------------------------------------------- */
    var sheetOpen = false;

    function openSheet() {
      if (!sheet || sheetOpen) return;
      sheetOpen = true;
      root.setAttribute('data-fa-sheet-open', '');
      if (burger) burger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      closeMega();
    }

    function closeSheet() {
      if (!sheet || !sheetOpen) return;
      sheetOpen = false;
      root.removeAttribute('data-fa-sheet-open');
      if (burger) burger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    if (burger) {
      burger.addEventListener('click', function () {
        if (sheetOpen) closeSheet();
        else openSheet();
      });
    }
    if (sheetClose) sheetClose.addEventListener('click', closeSheet);
    if (scrim) scrim.addEventListener('click', closeSheet);

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeSheet();
    });

    if (sheet) {
      sheet.addEventListener('click', function (event) {
        if (event.target.closest('a')) closeSheet();
      });
    }

    /* Accordion inside the sheet (real hidden, so never clickable) */
    var toggles = document.querySelectorAll('[data-fa-sheet-toggle]');
    Array.prototype.forEach.call(toggles, function (toggle) {
      toggle.addEventListener('click', function () {
        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        var panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (panel) panel.hidden = expanded;
      });
    });
  }

  ready(init);

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('[data-fa-header]')) {
      document.body.style.overflow = '';
      root.removeAttribute('data-fa-sheet-open');
      init();
    }
  });
})();
